/* eslint-disable no-param-reassign, sonarjs/cognitive-complexity, sort-imports */

import { getLogger } from 'loglevel';
import loadJs from './loadJs';
/**
 * Script to load and handle HubSpot forms.
 * @see https://developers.hubspot.com/docs/methods/forms/advanced_form_options
 */
export default class FormHubSpot {
  constructor() {
    this.selector = '.c-form-hubspot';
    this.logger = getLogger('c-form-hubspot');
    this.logger.setLevel('warn');
    this.numberInput = 'input[type="number"]';
    this.loader = '.c-form-hubspot__loader';

    this.classes = {
      errorMessage: 'c-error-messages',
      hidden: 'hidden',
    };
  }

  bootstrap() {
    this.loadScripts()
      .then(() => this.loadForms())
      .then(() => this.postLoadActions());

    this.registrationForm();
  }

  loadScripts() {
    this.logger.info('loadScripts', 'Loading HubSpot form scripts');

    return Promise.all([
      loadJs('https://js.hsforms.net/forms/v2-legacy.js'),
      loadJs('https://js.hsforms.net/forms/v2.js'),
    ]).catch(error => {
      this.logger.warn(
        'bootstrap',
        'Unable to load HubSpot form scripts',
        error,
      );
    });
  }

  loadForms() {
    $(this.selector).each((index, element) => {
      const $formDiv = $(element);

      this.loadForm($formDiv);
    });
  }

  loadForm($formDiv) {
    this.logger.info('loadForm', $formDiv);

    if (!window.hbspt.forms) {
      this.logger.warn(
        'loadForm',
        'Unable to load form. HubSpot Forms API script not available.',
      );

      return false;
    }
    const formData = this.getFormData($formDiv);

    window.hbspt.forms.create(formData);
  }

  getFormData($formDiv) {
    const formData = {
      target: `#${$formDiv.attr('id')}`,
      formInstanceId: $formDiv.attr('id'),
      portalId: $formDiv.data('portal-id'),
      formId: $formDiv.data('form-id'),
      submitButtonClass: $formDiv.data('submit-button-class'),
      errorClass: $formDiv.data('error-class'),
      translations: $formDiv.data('translations'),
      errorMessageClass: $formDiv.data('error-message-class'),
      onBeforeFormInit: ctx => this.onBeforeFormInit($formDiv, ctx),
      onFormReady: $form => this.onFormReady($formDiv, $form),
      onFormSubmit: $form => this.onFormSubmit($formDiv, $form),
      onFormSubmitted: () => this.onFormSubmitted($formDiv),
    };

    if ($formDiv.data('redirect-url')) {
      formData.redirectUrl = $formDiv.data('redirect-url');
    }

    if ($formDiv.data('include-styles') === false) {
      formData.css = '';
      formData.cssRequired = '';
    }

    return formData;
  }

  onBeforeFormInit($formDiv, ctx) {
    this.logger.info('onBeforeFormInit', $formDiv, ctx);
  }

  onFormReady($formDiv, $form) {
    this.logger.info('onFormReady', $formDiv, $form);

    const observer = new MutationObserver(mutationList => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(addedNode => {
            if (
              addedNode &&
              addedNode.nodeType !== 3 &&
              addedNode.classList.contains(this.classes.errorMessage)
            ) {
              addedNode.querySelectorAll('label').forEach(label => {
                const doc = new DOMParser().parseFromString(
                  label.innerHTML,
                  'text/html',
                );

                label.innerHTML = doc.documentElement.textContent; // eslint-disable-line no-param-reassign
              });
            }
          });
        }
      }
    });

    observer.observe($formDiv[0], { childList: true, subtree: true });
  }

  onFormSubmit($formDiv, $form) {
    this.logger.info('onFormSubmit', $formDiv, $form);
  }

  onFormSubmitted($formDiv) {
    this.logger.info('onFormSubmitted', $formDiv);

    const form = document.querySelector(this.selector);
    const $loader = document.querySelector(this.loader);

    const { scrollToTopOfPageAfterSubmit } = form.dataset;

    if (scrollToTopOfPageAfterSubmit === 'true') {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }

    if ($loader) {
      $loader.classList.remove(this.classes.hidden);
    }
  }

  postLoadActions() {
    const forms = [ ...document.querySelectorAll(this.selector) ];

    forms.forEach(form => {
      const { numberIncrements, isRegistrationForm } = form.dataset;

      if (isRegistrationForm === 'true') {
        this.initRegisterStepForm(form);
      }

      if (numberIncrements === 'true') {
        this.incrementNumber();
      }
    });
  }

  incrementNumber() {
    // When form(s) ready
    window.addEventListener('message', event => {
      if (
        event.data.type === 'hsFormCallback' &&
        event.data.eventName === 'onFormReady'
      ) {
        this.logger.info('postLoadActions', 'HubSpot form scripts loaded');

        $(this.selector).each((index, element) => {
          const $formDiv = $(element);

          // Set 0 as min to number inputs
          this.setMinToNumberInput($formDiv);

          // Transform number inputs
          this.transformNumberInputs($formDiv);

          // Disable number inputs
          $formDiv.find(this.numberInput).prop('disabled', true);
        });
      }
    });
  }

  /* eslint-disable class-methods-use-this */
  setMinToNumberInput($formDiv) {
    $formDiv.find(this.numberInput).attr('min', 0);
  }

  transformNumberInputs($formDiv) {
    $formDiv.find(this.numberInput).each((index, element) => {
      const $inputEl = $(element);
      const $inputWrapper = $inputEl.parent();

      $inputWrapper.prepend(
        '<div class="c-form-hubspot__input-number-btn c-form-hubspot__input-number__minus disabled">-</div>',
      );
      $inputWrapper.append(
        '<div class="c-form-hubspot__input-number-holder">0</div>',
      );
      $inputWrapper.append(
        '<div class="c-form-hubspot__input-number-btn c-form-hubspot__input-number__plus">+</div>',
      );

      $inputWrapper.on(
        'click',
        '.c-form-hubspot__input-number__minus',
        event => {
          const $input = $(event.target).parent().find(this.numberInput);
          const $numberHolder = $(event.target)
            .parent()
            .find('.c-form-hubspot__input-number-holder');

          if ($input.val() > 0) {
            $input.val(parseInt($input.val(), 10) - 1);
            $numberHolder.text($input.val());

            if ($input.val() === '0') {
              $(event.target).addClass('disabled');
            }
          }
        },
      );

      $inputWrapper.on(
        'click',
        '.c-form-hubspot__input-number__plus',
        event => {
          const $input = $(event.target).parent().find(this.numberInput);
          const $inputNumberHolder = $(event.target)
            .parent()
            .find('.c-form-hubspot__input-number-holder');

          $input.val(parseInt($input.val(), 10) + 1);
          $inputNumberHolder.text($input.val());

          $inputWrapper
            .find('.c-form-hubspot__input-number__minus')
            .removeClass('disabled');
        },
      );
    });
  }

  initRegisterStepForm(form) {
    const { formId } = form.dataset;

    window.addEventListener('message', event => {
      if (
        event.data.type === 'hsFormCallback' &&
        event.data.eventName === 'onFormReady' &&
        event.data.id === formId
      ) {
        const fieldEmail = form.querySelector('[name=email]');

        fieldEmail.addEventListener('change', fieldEvent => this.onEmailInput(form, fieldEvent.target.value || ''));

        this.onEmailInput(form, '');
      }
    });
  }

  getFieldsByName(form, names) {
    if (!Array.isArray(names)) {
      names = names.split(',');
    }

    return names.map(field => field.trim()).filter(Boolean).flatMap(field => [ ...form.querySelectorAll(`[name=${field}]`) ]).filter(Boolean);
  }

  onEmailInput(form, email) {
    const fieldSets = [ ...form.querySelectorAll('.form-columns-1,.form-columns-2') ];
    const fieldFreeEmail = form.querySelector('[name=free_email]');

    const fieldsCompanyNames = 'jobtitle,company,industry___business_segment,product_s_of_interest,what_phase_is_your_project_currently_in_,message';
    const fieldsCommunityNames = 'please_specify_what_is_your_intended_use_for_accessing_hailo_s_community_,what_hailo_8_product_are_you_using_';

    const fieldsCompany = this.getFieldsByName(form, fieldsCompanyNames);
    const fieldsCommunity = this.getFieldsByName(form, fieldsCommunityNames);

    const updateRowsVisibility = () => {
      fieldSets.forEach(fieldSet => {
        const allHidden = [ ...fieldSet.querySelectorAll('input,select,textarea') ].map(field => field.dataset.hidden === '1');

        if (!allHidden.every(Boolean)) {
          fieldSet.style.display = 'flex';
        } else {
          fieldSet.style.display = 'none';
        }
      });
    };

    fieldsCompany.forEach(field => {
      field.dataset.hidden = '1';
    });

    fieldsCommunity.forEach(field => {
      field.dataset.hidden = '1';
    });

    updateRowsVisibility();

    const isValidEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/iu.test(email); // eslint-disable-line prefer-named-capture-group

    if (isValidEmail) {
      fetch(`/wp-json/hailo/v1/is-free-email?${new URLSearchParams({ email })}`, {
        method: 'GET',
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            fieldsCompany.forEach(field => {
              field.dataset.hidden = '1';
            });

            fieldsCommunity.forEach(field => {
              field.dataset.hidden = '1';
            });

            fieldFreeEmail.value = 'No';

            if (data.data.is_free) {
              fieldsCommunity.forEach(field => {
                field.dataset.hidden = '0';
              });

              fieldFreeEmail.value = 'Yes';
            } else {
              fieldsCompany.forEach(field => {
                field.dataset.hidden = '0';
              });
            }

            updateRowsVisibility();
          }
        });
    }
  }

  registrationForm() {
    window.addEventListener('message', event => {
      const formId = event.data.id;
      const form = document.querySelector(`[data-form-id="${formId}"]`);

      if (!form) {
        return;
      }

      const { isRegistrationForm, nonce } = form.dataset;

      if (isRegistrationForm !== 'true') {
        return;
      }

      if (
        event.data.type === 'hsFormCallback' &&
        event.data.eventName === 'onFormSubmit'
      ) {
        const fields = event.data.data;

        const data = new FormData();

        data.append('action', 'af_dev_tools_register_user');
        data.append('id', event.data.id);
        data.append('nonce', nonce);

        fields.forEach(item => {
          data.append(item.name, item.value);
        });

        fetch(window.af_app.ajaxurl, {
          method: 'POST',
          body: data,
        }).then(result => result.json()).then(response => {
          if (response.redirect_url) {
            window.location.href = response.redirect_url;
          }
        });
      }
    });
  }
}
